import { InvoiceTypes } from "../types/InvoiceTypes";

interface InvoiceSettings {
	showLogo?: boolean;
	showSignature?: boolean;
	showBankDetails?: boolean;
	showNotes?: boolean;
	showFooter?: boolean;
}

// Helper function to convert newlines to HTML line breaks
const formatMultilineText = (text: string | undefined | null): string => {
	if (!text) return '';
	// Escape HTML first, then convert newlines to <br> tags
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#039;')
		.replace(/\n/g, '<br>');
};

const htmlContent = (
	currentInvoice: InvoiceTypes,
	subtotal: number,
	total: number,
	tax: number | string,
	activeCompany?: any,
	settings?: InvoiceSettings
) => {
	const invoiceSettings = settings || {
		showLogo: true,
		showSignature: true,
		showBankDetails: true,
		showNotes: true,
		showFooter: true,
	};

	return `
    <html>
      <head>
        <style>
      /* Modern Invoice Styling */
      @page {
        size: A4;
        margin: 0.5in;
      }

      @media print {
        @supports (-webkit-print-color-adjust: exact) {
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            width: 210mm;
            min-height: 297mm;
            outline: none;
            -webkit-print-color-adjust: exact;
          }
        }

        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 0;
          width: 210mm;
          min-height: 297mm;
          outline: none;
        }
        
        .invoice-container {
          box-shadow: none;
          margin: 0;
          max-width: none;
          width: 100%;
        }
      }

      body {
        font-family: Arial, sans-serif;
        width: 210mm;
        min-height: 297mm;
        margin: 0 auto;
        background-color: #ffffff;
        box-sizing: border-box;
      }

      .card-body {
        padding: 0;
      }

      .row {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        margin: 0;
      }

      .col-lg-8 {
        width: 100%;
        max-width: 100%;
        padding: 0;
      }

      .shadow-4 {
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      }

      .border {
        border: 1px solid #e5e7eb;
      }

      .radius-8 {
        border-radius: 8px;
      }

      .p-20 {
        padding: 15px;
      }

      .py-28 {
        padding-top: 20px;
        padding-bottom: 20px;
      }

      .px-20 {
        padding-left: 15px;
        padding-right: 15px;
      }

      .d-flex {
        display: flex;
      }

      .flex-wrap {
        flex-wrap: wrap;
      }

      .justify-content-between {
        justify-content: space-between;
      }

      .align-items-end {
        align-items: flex-end;
      }

      .gap-3 {
        gap: 12px;
      }

      .border-bottom {
        border-bottom: 1px solid #e5e7eb;
      }

      .text-xl {
        font-size: 1.25rem;
        font-weight: 600;
        margin: 0 0 8px 0;
        color: #1f2937;
      }

      .text-lg {
        font-size: 1.125rem;
        font-weight: 600;
        margin: 0 0 8px 0;
        color: #1f2937;
      }

      .text-md {
        font-size: 1rem;
        font-weight: 600;
        margin: 0 0 8px 0;
        color: #1f2937;
      }

      .text-sm {
        font-size: 0.875rem;
        margin: 0;
        color: #374151;
      }

      .mb-1 {
        margin-bottom: 4px;
      }

      .mb-0 {
        margin-bottom: 0;
      }

      .mb-8 {
        margin-bottom: 8px;
      }

      .mt-24 {
        margin-top: 16px;
      }

      .mt-64 {
        margin-top: 32px;
      }

      .text-secondary-light {
        color: #6b7280;
      }

      .text-primary-light {
        color: #3b82f6;
      }

      .fw-semibold {
        font-weight: 600;
      }

      .ps-8 {
        padding-left: 8px;
      }

      .pe-16 {
        padding-right: 16px;
      }

      .pe-64 {
        padding-right: 64px;
      }

      .pb-4 {
        padding-bottom: 4px;
      }

      .pt-4 {
        padding-top: 4px;
      }

      .px-12 {
        padding-left: 12px;
        padding-right: 12px;
      }

      .table {
        width: 100%;
        margin-bottom: 12px;
        border-collapse: collapse;
      }

      .bordered-table th,
      .bordered-table td {
        border: 1px solid #e5e7eb;
        padding: 8px 6px;
        text-align: left;
      }

      .bordered-table th {
        background-color: #f9fafb;
        font-weight: 600;
        color: #374151;
      }

      .text-end {
        text-align: right;
      }

      .text-center {
        text-align: center;
      }

      .border-top {
        border-top: 1px solid #000;
        display: inline-block;
        padding-top: 8px;
      }

      .d-inline-block {
        display: inline-block;
      }

      .table-responsive {
        overflow-x: auto;
      }

      .invoice-logo {
        max-width: 120px;
        height: auto;
      }

      .invoice-container {
        background: white;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        border-radius: 8px;
        overflow: hidden;
        margin: 0;
        max-width: 100%;
        width: 100%;
      }

      .invoice-header {
        background: #f9fafb;
        border-bottom: 1px solid #e5e7eb;
      }

      .info-table td {
        padding: 2px 0;
        vertical-align: top;
      }

      .info-table td:first-child {
        font-weight: 500;
        min-width: 90px;
      }

      .company-info {
        text-align: right;
      }

      @media (max-width: 768px) {
        .company-info {
          text-align: left;
          margin-top: 20px;
        }
      }
    </style>
    </head>
      
    <body>
      <div class="card-body">
        <div class="row">
          <div class="col-lg-8">
            <div class="invoice-container shadow-4 border radius-8">
              <div class="invoice-header p-20 d-flex flex-wrap justify-content-between gap-3 border-bottom">
                <div>
                  <h3 class="text-xl" data-field="invoice_template_name">${currentInvoice.invoice_template_name || activeCompany?.companyName || activeCompany?.name || 'Invoice'}</h3>
                  <p class="mb-1 text-sm" data-field="date">Date Issued: ${currentInvoice.date}</p>
                  <p class="mb-0 text-sm" data-field="invoiceId">Invoice ID: ${currentInvoice.invoiceId}</p>
                </div>
                <div class="company-info">
                  ${invoiceSettings.showLogo ? `<img src="${currentInvoice.invoice_logo || activeCompany?.logo || 'https://smartsaas.co.uk/wp-content/uploads/2023/07/smartsaas-logo-complete_tiny_ps_tiny_ps_tiny_ps.svg'}" alt="Company Logo" class="invoice-logo mb-8" data-field="invoice_logo">` : ''}
                  <p class="mb-1 text-sm" data-field="address">${formatMultilineText(currentInvoice.address || activeCompany?.address || '')}</p>
                  <p class="mb-0 text-sm"><span data-field="email">${currentInvoice.from_email || currentInvoice.email || activeCompany?.email || ''}</span>, <span data-field="phone">${currentInvoice.contact_information?.phone || activeCompany?.phone || ''}</span></p>
                </div>
              </div>
              
              <div class="py-28 px-20">
                <div class="d-flex flex-wrap justify-content-between align-items-end gap-3">
                  <div>
                    <h6 class="text-md">Billed To:</h6>
                    <table class="info-table text-sm text-secondary-light">
                      <tbody>
                        <tr>
                          <td>Name</td>
                          <td class="ps-8" data-field="toCompanyName">: ${currentInvoice.toCompanyName || currentInvoice.toName || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td>Address</td>
                          <td class="ps-8" data-field="toAddress">: ${formatMultilineText(currentInvoice.toAddress || 'N/A')}</td>
                        </tr>
                        <tr>
                          <td>Country</td>
                          <td class="ps-8" data-field="country_code">: ${currentInvoice.country_code || 'N/A'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  ${invoiceSettings.showBankDetails ? `
                  <div>
                    <table class="info-table text-sm text-secondary-light">
                      <tbody>
                        <tr>
                          <td>Bank Name</td>
                          <td class="ps-8" data-field="bankName">: ${currentInvoice.bankName || activeCompany?.bankName || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td>Account Number</td>
                          <td class="ps-8" data-field="bankAccountNumber">: ${currentInvoice.bankAccountNumber || activeCompany?.bankAccountNumber || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td>Sort Code</td>
                          <td class="ps-8" data-field="bankSortcode">: ${currentInvoice.bankSortcode || activeCompany?.bankSortcode || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td>IBAN</td>
                          <td class="ps-8" data-field="bankIban">: ${currentInvoice.bankIban || activeCompany?.bankIban || 'N/A'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  ` : ''}
                </div>

                <div class="mt-24">
                  <div class="table-responsive">
                    <table class="table bordered-table text-sm">
                      <thead>
                        <tr>
                          <th scope="col" class="text-sm">SL.</th>
                          <th scope="col" class="text-sm">Items</th>
                          <th scope="col" class="text-sm">Description</th>
                          <th scope="col" class="text-sm">Qty</th>
                          <th scope="col" class="text-sm">Unit Price</th>
                          <th scope="col" class="text-end text-sm">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${(Array.isArray(currentInvoice.items) ? currentInvoice.items : [])
                          .map(
                            (item, index) => `
                            <tr>
                              <td>${String(index + 1).padStart(2, '0')}</td>
                              <td>${item.label || 'N/A'}</td>
                              <td>${(item.short_description || item.description || 'No description').substring(0, 50)}${(item.short_description || item.description || '').length > 50 ? '...' : ''}</td>
                              <td>${item.quantity || 1}</td>
                              <td>${currentInvoice.currencyCode || '$'}${(Number(item.price) || 0).toFixed(2)}</td>
                              <td class="text-end">${currentInvoice.currencyCode || '$'}${((Number(item.quantity) || 1) * (Number(item.price) || 0)).toFixed(2)}</td>
                            </tr>
                          `
                          )
                          .join('')}
                      </tbody>
                    </table>
                  </div>
                  
                  <div class="d-flex flex-wrap justify-content-between gap-3">
                    ${invoiceSettings.showNotes ? `
                    <div>
                      <p class="text-sm mb-0"><span class="text-primary-light fw-semibold">Notes:</span></p>
                      <p class="text-sm mb-0" data-field="notes">${formatMultilineText(currentInvoice.notes || 'Thank you for your business')}</p>
                    </div>
                    ` : '<div></div>'}
                    <div>
                      <table class="text-sm">
                        <tbody>
                          <tr>
                            <td class="pe-64">Subtotal:</td>
                            <td class="pe-16" data-field="subtotal">
                              <span class="text-primary-light fw-semibold">${currentInvoice.currencyCode || '$'}${subtotal}</span>
                            </td>
                          </tr>
                          <tr>
                            <td class="pe-64">Tax:</td>
                            <td class="pe-16" data-field="tax">
                              <span class="text-primary-light fw-semibold">${currentInvoice.currencyCode || '$'}${tax}</span>
                            </td>
                          </tr>
                          <tr>
                            <td class="pe-64 border-bottom pb-4">Discount:</td>
                            <td class="pe-16 border-bottom pb-4">
                              <span class="text-primary-light fw-semibold">${currentInvoice.currencyCode || '$'}0.00</span>
                            </td>
                          </tr>
                          <tr>
                            <td class="pe-64 pt-4">
                              <span class="text-primary-light fw-semibold">Total:</span>
                            </td>
                            <td class="pe-16 pt-4" data-field="total">
                              <span class="text-primary-light fw-semibold">${currentInvoice.currencyCode || '$'} ${Number(total).toFixed(2)}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                ${invoiceSettings.showSignature ? `
                <div class="d-flex flex-wrap justify-content-between align-items-end mt-64">
                  <div class="text-sm border-top d-inline-block px-12">Signature of Customer</div>
                  <div class="text-sm border-top d-inline-block px-12">Signature of Authorized</div>
                </div>
                ` : ''}
                ${invoiceSettings.showFooter ? `
                <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background-color: rgba(145, 157, 231, 1.00); padding: 10px; margin-top: 40px;">
                  <tr style="margin-block:10px; margin-bottom: 20px;">
                    <td align="center">
                      <a href="https://smartsaas.co.uk" style="text-decoration: none; display: inline-block; vertical-align: middle; margin-right: 10px;">
                        <img
                          alt="smartsaas logo"
                          style="height: 40px; vertical-align: middle;"
                          src="https://storage.googleapis.com/smartsaas-company-logo-store/662f542a36b10d08e50fd222-logo.png"
                        />
                      </a>
                      <span style="
                            font-size: 14px;
                            font-family: 'Gill Sans', 'Gill Sans MT', 'Myriad Pro', 'DejaVu Sans Condensed', Helvetica, Arial, sans-serif;
                            color: #fff;
                            display: inline-block;
                            vertical-align: middle;
                          ">
                        Invoice generated by
                        <a href="https://smartsaas.co.uk" style="color: #fff; text-decoration: underline;">SmartSaaS</a>.
                      </span>
                    </td>
                  </tr>
                </table>
                ` : ''}
              </div>
            </div>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};

export default htmlContent;
